<svelte:options
  customElement={{
    tag: "oc-interactive-overlay-v1",
    /*                       */
    extend: window.__components.extend(),
    props: {
      base64Href: { type: "String", attribute: "base64-href" },
      ocAriaLabel: { type: "String", attribute: "oc-aria-label" },
      rel: { type: "String" },
      target: { type: "String" },
      borderRadius: {
        type: "String",
        attribute: "border-radius",
      },
      borderOffset: {
        type: "String",
        attribute: "border-offset",
      },
    },
  }}
/>

<script lang="ts">
  import type { Props } from "./InteractiveOverlayV1.types";
  import { InteractiveElement } from "../../../common/components/InteractiveElement";
  import { tryGetSingleAssignedElement } from "./tryGetSingleAssignedElement";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";

  let {
    base64Href = undefined,
    target = undefined,
    rel = undefined,
    ocAriaLabel = undefined,
    borderRadius = undefined,
    borderOffset = undefined,
  }: Props = $props();

  const Host = $host<HTMLElement>();

  let internalBorderRadius: string | undefined = $state(undefined);
  let internalBorderOffset: string | undefined = $state(undefined);

  const getStyleValue = (
    cssVar: string,
    defaultCssVar: string,
    prop?: string,
    firstChildStyle?: string,
  ): string =>
    /*                    */
    prop ||
    /*                            */
    getComputedStyle(Host).getPropertyValue(cssVar).trim() ||
    /*                                */
    (firstChildStyle && firstChildStyle !== "0px"
      ? firstChildStyle
      : /*                  */
        getComputedStyle(Host).getPropertyValue(defaultCssVar).trim());

  const updateBorderSettings = (): void => {
    let singleAssignedElement = tryGetSingleAssignedElement(
      Host.shadowRoot!.getElementById("slot") as HTMLSlotElement,
    );

    let firstChildStyles = singleAssignedElement && getComputedStyle(singleAssignedElement);

    internalBorderRadius = getStyleValue(
      "--border-radius",
      "--default-border-radius",
      borderRadius,
      firstChildStyles?.borderRadius,
    );

    internalBorderOffset = getStyleValue(
      "--border-offset",
      "--default-border-offset",
      borderOffset,
      firstChildStyles?.outlineOffset,
    );
  };
</script>

<InteractiveElement
  {Host}
  asButton={true}
  bind:base64Href
  {target}
  {rel}
  class="root"
  {ocAriaLabel}
  style={`--internal-border-radius: calc(${internalBorderOffset} + ${internalBorderRadius}); --internal-border-offset: ${internalBorderOffset};`}
  onmouseenter={updateBorderSettings}
  onfocus={updateBorderSettings}
>
  {#snippet children(onslotchange)}
    <slot id="slot" {onslotchange} />
  {/snippet}
</InteractiveElement>

<DefaultStyles display="block" />

<style lang="scss">
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "@otto-ec/otto-components-utils/scss/mixins";
  @use "../../floating-focus/v1/FloatingFocusV1.shared" as floating-focus;
  :global {
    :host {
      position: relative;
      width: fit-content;

      /*        */
      --border-radius: initial;
      --border-offset: initial;

      --default-border-radius: #{tokens.$oc-component-interactive-overlay-default-border-radius};
      --default-border-offset: #{tokens.$oc-component-interactive-overlay-default-offset};
    }

    .root {
      &,
      ::slotted(a) {
        display: block;
        text-decoration: inherit;
        color: inherit;
        background: none;
        border: none;
        padding: 0;
        outline: none;
        outline-offset: 0; /*                                        */
        font: inherit;
        cursor: pointer;
        height: 100%; /*                              */
      }

      /*                                           */
      &.-interactive,
      ::slotted(a) {
        /*                    */
        @include floating-focus.full-bleed();

        --oc-floating-focus-v1-radius: var(--internal-border-radius);
        --oc-floating-focus-v1-offset: var(--internal-border-offset);

        &:before {
          content: "";
          position: absolute;
          inset: 0;
        }
      }

      /*                  */
      @media (hover: hover) {
        &.-interactive:hover,
        ::slotted(a:hover) {
          &:after {
            content: "";
            position: absolute;
            inset: calc(-1 * var(--internal-border-offset));
            border-radius: var(--internal-border-radius);
            background-color: tokens.$oc-component-interactive-overlay-background-color-hover;
            pointer-events: none;
          }
        }
      }

      /*                  */
      &.-interactive:active,
      ::slotted(a:active) {
        &:after {
          content: "";
          position: absolute;
          inset: calc(-1 * var(--internal-border-offset));
          border-radius: var(--internal-border-radius);
          background-color: tokens.$oc-component-interactive-overlay-background-color-active !important;
          pointer-events: none;
        }
      }
    }
  }
</style>
